import { useState } from 'react';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { FieldError } from '../../components/ui/FieldError';
import { ApiError } from '../../lib/api-client';
import { confirmAction } from '../../lib/confirm-service';
import { useToast } from '../../lib/toast';
import type { BillableAdmission } from './billable-admission';
import { useAddIpdBillReceipt, useIpdBills } from './ipd-daycare.queries';
import { currency } from './ipd-daycare.utils';
import type { IpdBillPaymentMode } from './types';

const paymentModes: IpdBillPaymentMode[] = ['Cash', 'UPI', 'Transfer to Bank Account', 'Cheque', 'Other', 'Online'];

export const IpdNewReceiptForm = ({ admission, onRecorded }: { admission: BillableAdmission; onRecorded: (billId: string) => void }) => {
  const billsQuery = useIpdBills(admission.id);
  const [mode, setMode] = useState<IpdBillPaymentMode>('Cash');
  const [bankName, setBankName] = useState('');
  const [amount, setAmount] = useState(0);
  const [showAmountError, setShowAmountError] = useState(false);
  const mutation = useAddIpdBillReceipt();
  const { showToast } = useToast();

  if (billsQuery.isLoading) return <LoadingState />;
  if (billsQuery.isError) return <ErrorState title="Unable to load bills" message={billsQuery.error instanceof ApiError ? billsQuery.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void billsQuery.refetch() }} />;
  const bills = (billsQuery.data ?? []).filter((bill) => bill.status !== 'cancelled');
  const selectedBill = bills[0];

  if (!selectedBill) return <EmptyState title="No bills yet" message="Create a bill for this admission before recording a receipt." />;

  const submit = async () => {
    setShowAmountError(true);
    if (amount <= 0) return;
    const confirmed = await confirmAction({
      title: 'Confirm Receipt',
      description: `Are you sure you want to add a receipt of ${currency(amount)} to bill #${selectedBill.billNumber}?`,
      confirmLabel: 'Yes, Add Receipt',
    });
    if (!confirmed) return;
    mutation.mutate({ billId: selectedBill.id, payload: { mode, bankName: bankName.trim() || null, amount } }, {
      onSuccess: () => {
        setAmount(0);
        setBankName('');
        setShowAmountError(false);
        showToast('Receipt saved successfully.', 'success');
        onRecorded(selectedBill.id);
      },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The receipt could not be recorded.', 'error'),
    });
  };

  return (
    <>
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>New Receipt — {admission.patientSnapshot.name}</span></div>
        <p>{admission.admissionLabel}</p>
      </header>
      <section className="opd-bill-subsection">
        <h3>Record a payment</h3>
        <div className="ipd-daycare-admission-row">
          <label>
            <span className="ipd-daycare-label-text">Bill</span>
            <input value={`Bill #${selectedBill.billNumber} — Due ${currency(selectedBill.dueAmount)}`} disabled />
          </label>
          <label><span className="ipd-daycare-label-text">Mode</span><select value={mode} onChange={(event) => setMode(event.target.value as IpdBillPaymentMode)}>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label><span className="ipd-daycare-label-text">Bank</span><input value={bankName} onChange={(event) => setBankName(event.target.value)} /></label>
          <label><span className="ipd-daycare-label-text">Amount</span><input type="number" min={0} step="0.01" value={amount} aria-invalid={showAmountError && amount <= 0} onChange={(event) => setAmount(Number(event.target.value))} /><FieldError message={showAmountError && amount <= 0 ? 'Amount must be greater than zero.' : undefined} /></label>
        </div>
        <div className="opd-form-actions">
          <button className="button button-primary" type="button" disabled={mutation.isPending} onClick={() => void submit()}>{mutation.isPending ? 'Saving…' : 'Add Receipt'}</button>
        </div>
      </section>
    </>
  );
};
